<template>
  <div class="page-container">
    <div class="table-wrapper">
      <div class="table-header">
        <span class="table-title">支付平台配置</span>
        <div class="table-actions">
          <button v-auth="'payment:payConfig:save'" class="btn btn-primary btn-sm" @click="openAdd">+ 新增支付平台</button>
        </div>
      </div>
      <el-table :data="tableData" border v-loading="loading" style="width:100%;">
        <el-table-column prop="sort" label="排序" width="80" align="center">
          <template slot-scope="{row}">{{ row.sort }}</template>
        </el-table-column>
        <el-table-column prop="platformName" label="支付平台" width="120">
          <template slot-scope="{row}">{{ row.platformName || '--' }}</template>
        </el-table-column>
        <el-table-column prop="platformTypeName" label="支付平台类型" width="120">
          <template slot-scope="{row}">{{ row.platformTypeName || '--' }}</template>
        </el-table-column>
        <el-table-column prop="merchantNo" label="支付平台商户号（MID）" min-width="150">
          <template slot-scope="{row}"><span class="mono">{{ row.merchantNo }}</span></template>
        </el-table-column>
        <el-table-column prop="appId" label="支付平台应用ID" width="130">
          <template slot-scope="{row}">{{ row.appId || '--' }}</template>
        </el-table-column>
        <el-table-column prop="createUser" label="创建人" width="100">
          <template slot-scope="{row}">{{ row.createUser || '--' }}</template>
        </el-table-column>
        <el-table-column prop="updateUser" label="最后更新人" width="110">
          <template slot-scope="{row}">{{ row.updateUser || '--' }}</template>
        </el-table-column>
        <el-table-column label="创建时间" width="160">
          <template slot-scope="{row}">{{ fmtDateTime(row.createDate) }}</template>
        </el-table-column>
        <el-table-column label="最后更新时间" width="160">
          <template slot-scope="{row}">{{ fmtDateTime(row.updateDate) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="{row}">
            <el-switch v-auth="'payment:payConfig:status'" v-model="row.isDelete" :active-value="1" :inactive-value="2" active-color="var(--brand-primary)" @change="toggleStatus(row)"></el-switch>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right" align="center">
          <template slot-scope="{row}">
            <el-button v-auth="'payment:payConfig:find'" type="text" size="small" style="color:var(--brand-primary);" @click="openDetail(row)">详情</el-button>
            <el-button v-auth="'payment:payConfig:edit'" type="text" size="small" style="color:var(--brand-primary);" @click="openEdit(row)">编辑</el-button>
            <el-button v-auth="'payment:payConfig:delete'" type="text" size="small" style="color:#dc2626;" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination @size-change="(v)=>{pageSize=v;pageNum=1;fetchList()}" @current-change="(v)=>{pageNum=v;fetchList()}"
        :current-page="pageNum" :page-sizes="[15,30,50]" :page-size="pageSize" layout="total,sizes,prev,pager,next,jumper" :total="total"></el-pagination>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :visible.sync="editVisible" width="820px" :close-on-click-modal="false" :title="editForm.id ? '编辑支付平台' : '新增支付平台'">
      <el-form :model="editForm" label-width="130px" size="small" :rules="editRules" ref="editForm">
        <div class="form-section-title">① 基础信息</div>
        <div class="form-grid">
          <el-form-item label="支付平台" prop="platform">
            <el-select v-model="editForm.platform" style="width:100%;" placeholder="请选择支付平台">
              <el-option v-for="p in platformOptions" :key="p.code" :label="p.name" :value="p.code"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="支付平台商户号（MID）" prop="merchantNo">
            <el-input v-model="editForm.merchantNo" placeholder="请输入商户号"></el-input>
          </el-form-item>
          <el-form-item label="支付平台应用ID" prop="appId">
            <el-input v-model="editForm.appId" placeholder="请输入应用ID"></el-input>
          </el-form-item>
          <el-form-item label="支付平台类型" prop="platformType">
            <el-select v-model="editForm.platformType" style="width:100%;" placeholder="请选择平台类型">
              <el-option v-for="t in typeOptions" :key="t.code" :label="t.name" :value="t.code"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="通道排序" prop="sort">
            <el-input-number v-model="editForm.sort" :min="0" :precision="0" style="width:100%;"></el-input-number>
          </el-form-item>
          <el-form-item label="启用状态" prop="isDelete">
            <el-radio-group v-model="editForm.isDelete">
              <el-radio :label="1">启用</el-radio>
              <el-radio :label="2">禁用</el-radio>
            </el-radio-group>
          </el-form-item>
        </div>

        <div class="form-section-title">② 第三方配置（由支付平台提供）</div>
        <el-form-item label="秘钥" prop="thirdPartyApiKey">
          <el-input v-model="editForm.thirdPartyApiKey" type="password" show-password placeholder="请输入秘钥"></el-input>
        </el-form-item>
        <el-form-item label="回调秘钥">
          <el-input v-model="editForm.thirdPartyApiSecret" type="password" show-password placeholder="请输入回调秘钥"></el-input>
          <div v-if="editForm.id" class="form-hint">编辑时默认不回显密钥，如需修改请重新填写</div>
        </el-form-item>

        <div class="form-section-title">③ 第三方证书配置（用于验签）</div>
        <el-form-item label="公钥（由支付平台提供）" prop="counterpartyPublicKey">
          <el-input v-model="editForm.counterpartyPublicKey" type="textarea" :rows="4" placeholder="-----BEGIN PUBLIC KEY-----"></el-input>
        </el-form-item>

        <div class="form-section-title">④ 商户证书配置</div>
        <el-form-item label="公钥（由商户提供）" prop="ourPublicKey">
          <el-input v-model="editForm.ourPublicKey" type="textarea" :rows="4" placeholder="-----BEGIN PUBLIC KEY-----"></el-input>
        </el-form-item>
        <el-form-item label="商户私钥" prop="ourPrivateKey">
          <el-input v-model="editForm.ourPrivateKey" type="textarea" :rows="4" placeholder="-----BEGIN PRIVATE KEY-----"></el-input>
          <div class="cert-actions">
            <el-button type="primary" size="small" @click="generateKeyPair">生成证书</el-button>
          </div>
        </el-form-item>

        <div class="form-section-title">⑤ 接口地址配置（收银台接口地址）</div>
        <div class="form-grid">
          <el-form-item label="入金" prop="depositUrl">
            <el-input v-model="editForm.depositUrl" placeholder="https://"></el-input>
          </el-form-item>
          <el-form-item label="出金" prop="withdrawUrl">
            <el-input v-model="editForm.withdrawUrl" placeholder="https://"></el-input>
          </el-form-item>
          <el-form-item label="出入金查单" prop="depositQueryUrl">
            <el-input v-model="editForm.depositQueryUrl" placeholder="https://"></el-input>
          </el-form-item>
        </div>
        <el-form-item label="业务回调通知地址" prop="notifyUrl">
          <el-input v-model="editForm.notifyUrl" placeholder="https://（外网可达的回调域名，自动拼接 /payment/notify/{channelCode}）"></el-input>
        </el-form-item>
        <el-form-item label="支付成功跳转地址" prop="returnUrl">
          <el-input v-model="editForm.returnUrl" placeholder="https://（支付成功后浏览器跳转的前端页面URL，可选，不配则下单不传 return_url）"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="editVisible=false">取消</el-button>
        <el-button type="primary" @click="submitSave">{{ editForm.id ? '保存' : '确定' }}</el-button>
      </div>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog :visible.sync="detailVisible" width="640px" :close-on-click-modal="false" title="支付平台详情">
      <div v-if="detail" class="detail-body">
        <div class="detail-section">
          <div class="detail-title">基础信息</div>
          <div class="detail-grid">
            <div><span class="detail-label">支付平台应用ID：</span><span class="detail-strong">{{ detail.appId || '--' }}</span></div>
            <div><span class="detail-label">支付平台商户号（MID）：</span><span class="mono">{{ detail.merchantNo }}</span></div>
            <div><span class="detail-label">支付平台类型：</span>{{ detail.platformTypeName || '--' }}</div>
            <div><span class="detail-label">通道排序：</span>{{ detail.sort }}</div>
            <div><span class="detail-label">启用状态：</span><span :style="{ color: detail.isDelete === 1 ? '#16a34a' : '#9ca3af' }">{{ detail.isDelete === 1 ? '启用' : '禁用' }}</span></div>
            <div><span class="detail-label">创建人：</span>{{ detail.createUser || '--' }}</div>
            <div><span class="detail-label">最后更新人：</span>{{ detail.updateUser || '--' }}</div>
            <div><span class="detail-label">创建时间：</span>{{ fmtDateTime(detail.createDate) }}</div>
            <div><span class="detail-label">最后更新时间：</span>{{ fmtDateTime(detail.updateDate) }}</div>
          </div>
        </div>
        <div class="detail-section">
          <div class="detail-title">第三方配置（由支付平台提供）</div>
          <div class="detail-grid">
            <div><span class="detail-label">秘钥：</span><span class="mono">{{ detail.thirdPartyApiKey || '--' }}</span></div>
            <div><span class="detail-label">回调秘钥：</span><span class="mono">{{ detail.thirdPartyApiSecret || '--' }}</span></div>
          </div>
        </div>
        <div class="detail-section">
          <div class="detail-title">第三方证书配置（用于验签）</div>
          <div class="detail-grid">
            <div><span class="detail-label">公钥（由支付平台提供）：</span><span class="mono key-text">{{ maskKey(detail.counterpartyPublicKey) }}</span></div>
            <div><span class="detail-label">公钥（由商户提供）：</span><span class="mono key-text">{{ maskKey(detail.ourPublicKey) }}</span></div>
          </div>
        </div>
        <div class="detail-section">
          <div class="detail-title">接口地址配置</div>
          <div class="detail-grid">
            <div><span class="detail-label">入金：</span><span class="mono url-text">{{ detail.depositUrl || '--' }}</span></div>
            <div><span class="detail-label">出金：</span><span class="mono url-text">{{ detail.withdrawUrl || '--' }}</span></div>
            <div><span class="detail-label">出入金查单：</span><span class="mono url-text">{{ detail.depositQueryUrl || '--' }}</span></div>
            <div><span class="detail-label">业务回调通知地址：</span><span class="mono url-text">{{ detail.notifyUrl || '--' }}</span></div>
            <div><span class="detail-label">支付成功跳转地址：</span><span class="mono url-text">{{ detail.returnUrl || '--' }}</span></div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailVisible=false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { payConfigList, payConfigAddOrUpdate, payConfigDelete, payConfigStatus, payConfigGenerateKeyPair } from '@/api/payConfig'

export default {
  name: 'PayConfigList',
  data() {
    return {
      tableData: [], loading: false, total: 0,
      pageNum: 1, pageSize: 15,
      platformOptions: [
        { code: 2, name: 'MaxPay' },
        { code: 3, name: '银联商务' },
        { code: 4, name: 'Exlink' }
      ],
      typeOptions: [
        { code: 1, name: '第三方平台' },
        { code: 2, name: '银行直连' },
        { code: 3, name: '数字货币' }
      ],
      editVisible: false,
      editForm: {},
      editRules: {
        platform: [{ required: true, message: '请选择支付平台', trigger: 'change' }],
        platformType: [{ required: true, message: '请选择支付平台类型', trigger: 'change' }],
        merchantNo: [{ required: true, message: '请输入支付平台商户号（MID）', trigger: 'blur' }],
        depositUrl: [{ required: true, message: '请输入入金接口地址', trigger: 'blur' }],
        withdrawUrl: [{ required: true, message: '请输入出金接口地址', trigger: 'blur' }]
      },
      detailVisible: false,
      detail: null
    }
  },
  created() { this.fetchList() },
  methods: {
    fetchList() {
      this.loading = true
      payConfigList({ pageNum: this.pageNum, pageSize: this.pageSize }).then(res => {
        if (res.code === 0) { this.tableData = res.result.list || []; this.total = res.result.total || 0 }
      }).finally(() => { this.loading = false })
    },
    refreshList() {
      this.pageNum = 1
      this.fetchList()
      this.$message.success('刷新成功')
    },
    openAdd() {
      this.editForm = { id: null, platform: null, platformType: null, merchantNo: '', appId: '', thirdPartyApiKey: '', thirdPartyApiSecret: '', counterpartyPublicKey: '', ourPublicKey: '', ourPrivateKey: '', depositUrl: '', withdrawUrl: '', depositQueryUrl: '', notifyUrl: '', returnUrl: '', sort: 0, isDelete: 1 }
      this.editVisible = true
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        platform: row.platform,
        platformType: row.platformType,
        merchantNo: row.merchantNo,
        appId: row.appId || '',
        thirdPartyApiKey: row.thirdPartyApiKey || '',
        thirdPartyApiSecret: row.thirdPartyApiSecret || '',
        counterpartyPublicKey: row.counterpartyPublicKey || '',
        ourPublicKey: row.ourPublicKey || '',
        ourPrivateKey: row.ourPrivateKey || '',
        depositUrl: row.depositUrl || '',
        withdrawUrl: row.withdrawUrl || '',
        depositQueryUrl: row.depositQueryUrl || '',
        notifyUrl: row.notifyUrl || '',
        returnUrl: row.returnUrl || '',
        sort: row.sort || 0,
        isDelete: row.isDelete
      }
      this.editVisible = true
    },
    submitSave() {
      this.$refs.editForm.validate(valid => {
        if (!valid) return
        const f = this.editForm
        payConfigAddOrUpdate(f).then(res => {
          if (res.code === 0) {
            this.$message.success(f.id ? '保存成功' : '新增成功')
            this.editVisible = false
            this.fetchList()
          } else this.$message.error(res.message || '操作失败')
        })
      })
    },
    handleDelete(row) {
      this.$confirm('确认删除支付平台「' + (row.platformName || row.merchantNo) + '」？删除后不可恢复。', '删除确认', { type: 'warning' }).then(() => {
        payConfigDelete(row.id).then(res => {
          if (res.code === 0) { this.$message.success('删除成功'); this.fetchList() }
          else this.$message.error(res.message || '操作失败')
        })
      }).catch(() => {})
    },
    toggleStatus(row) {
      payConfigStatus(row.id, row.isDelete).then(res => {
        if (res.code === 0) {
          this.$message.success(row.isDelete === 1 ? '已启用' : '已禁用')
          this.fetchList()
        } else this.$message.error(res.message || '操作失败')
      })
    },
    generateKeyPair() {
      payConfigGenerateKeyPair().then(res => {
        if (res.code !== 0) {
          this.$message.error(res.message || '密钥对生成失败')
          return
        }
        this.editForm.ourPrivateKey = res.result.privateKey
        this.editForm.ourPublicKey = res.result.publicKey
        this.$message.success('密钥对已生成，请将公钥同步给收银台方')
      })
    },
    openDetail(row) {
      this.detail = row
      this.detailVisible = true
    },
    maskKey(s) {
      if (!s) return '--'
      if (s.length <= 50) return s
      return s.substring(0, 30) + '...' + s.substring(s.length - 20)
    },
    fmtDateTime(t) { return t ? String(t).replace('T', ' ').substring(0, 19) : '--' }
  }
}
</script>

<style scoped>
.page-container { background: #f4f6fb; min-height: calc(100vh - 84px); padding: 16px; }
.table-wrapper { background: #fff; border-radius: 10px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.06); }
.table-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.table-title { font-size: 15px; font-weight: 600; color: #1a2236; }
.table-actions { display: flex; gap: 8px; align-items: center; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; font-family: inherit; cursor: pointer; transition: all 0.15s; border: 1px solid transparent; white-space: nowrap; }
.btn-primary { background: var(--brand-primary); color: #fff; border-color: var(--brand-primary); }
.btn-secondary { background: transparent; color: #5a6578; border-color: #e8ecf2; }
.btn-sm { padding: 5px 10px; font-size: 12px; }
.mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; }
.form-section-title { font-size: 14px; font-weight: 600; color: #1a2236; margin: 8px 0 12px; padding-bottom: 8px; border-bottom: 1px solid var(--brand-primary); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; }
.form-hint { font-size: 12px; color: #9ca3af; margin-top: 4px; }
.cert-actions { display: flex; justify-content: flex-end; margin-top: 8px; }
.detail-body { padding: 0 4px; }
.detail-section { margin-bottom: 20px; }
.detail-title { font-size: 14px; font-weight: 600; color: #1a2236; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #e8ecf2; }
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; font-size: 13px; }
.detail-label { color: #9ca3af; }
.detail-strong { font-weight: 500; }
.key-text { font-size: 11px; word-break: break-all; }
.url-text { font-size: 11px; word-break: break-all; }
</style>
